import { z } from "zod";
import { timestampSchema } from "./timestamp";
import { choreKindSchema, choreSizeSchema } from "./chore";

/**
 * Instant submission — dziecko zgłasza wykonanie zadania OD RAZU (foto +
 * tytuł + kind + stawka). Parent zatwierdza jednym kliknięciem co tworzy
 * atomowo chore + completion + transakcję (jeśli extra).
 *
 * Różnica od `ChoreProposal`: tam dziecko proponuje zadanie na przyszłość
 * (parent musi zatwierdzić, potem dziecko wykonuje, potem parent zatwierdza
 * wykonanie — 4 kroki). Instant submission to 2 kroki: dziecko od razu z
 * fotem, parent jedno zatwierdzenie.
 *
 * Foto jest WYMAGANE (anti-abuse) — bez fotodowodu submission nie powstanie.
 * Limit 10/dzień per dziecko, max 100 zł stawki — większe wymaga normalnej
 * ścieżki przez ProposeChoreDialog → ApproveProposalDialog.
 */
export const instantSubmissionStatusSchema = z.enum([
  "pending",
  "approved",
  "rejected",
]);
export type InstantSubmissionStatus = z.infer<
  typeof instantSubmissionStatusSchema
>;

export const instantSubmissionSchema = z.object({
  id: z.string(),
  title: z.string().min(1).max(80),
  icon: z.string().max(40).optional(),
  kind: choreKindSchema,
  /** Stawka w groszach. Dla `family` zwykle 0 (XP only, bez transakcji). */
  rewardMinor: z.number().int().min(0).max(10_000),
  /** Sugerowany rozmiar wysiłku (AI szacuje przy create lub fallback "M"). */
  size: choreSizeSchema.optional(),
  /** Wymagane — bez fota submission się nie tworzy (rules egzekwują). */
  proofPhotoUrl: z.string().min(1),
  proofPhotoPath: z.string().min(1),
  submittedBy: z.string(),
  submittedAt: timestampSchema,
  status: instantSubmissionStatusSchema,
  /** Po approve — linki do utworzonych docs (ID chore'a + completion). */
  createdChoreId: z.string().optional(),
  createdCompletionId: z.string().optional(),
  reviewedBy: z.string().optional(),
  reviewedAt: timestampSchema.optional(),
  rejectionReason: z.string().max(200).optional(),
});
export type InstantSubmission = z.infer<typeof instantSubmissionSchema>;

/** Input do `createInstantSubmission` — tylko pola wpisywane przez dziecko. */
export const instantSubmissionInputSchema = z.object({
  title: z.string().min(1).max(80),
  icon: z.string().max(40).optional(),
  kind: choreKindSchema,
  rewardMinor: z.number().int().min(0).max(10_000),
  size: choreSizeSchema.optional(),
  proofPhotoUrl: z.string().min(1),
  proofPhotoPath: z.string().min(1),
});
export type InstantSubmissionInput = z.infer<
  typeof instantSubmissionInputSchema
>;
